import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { ListChecks } from 'lucide-react'
import type { MontageMangelZeile } from '@symphenia/shared-types'
import { api } from '@/lib/axios'
import { fmtDate } from '@/lib/format-date'
import { formCard } from '@/lib/form-styles'

/**
 * AP-47 6.2 — **Karte „Mängel" im Projekt** (nur mit Projektverwaltung und Montageprotokoll): die offenen Mängel
 * der Montageprotokolle dieses Projekts, ein Klick öffnet das Protokoll. Liegt unter `components/`, weil das
 * Projekt nicht in ein anderes Feature greifen darf (AP-21-B); gelesen wird über den Endpunkt der Mängelliste.
 */
export function ProjektMaengel({ projectId }: { projectId: string }) {
  const { t } = useTranslation()
  const [zeilen, setZeilen] = useState<MontageMangelZeile[] | null>(null)
  const [gesamt, setGesamt] = useState(0)
  const [fehler, setFehler] = useState(false)

  useEffect(() => {
    let aktiv = true
    api
      .get<{ data: MontageMangelZeile[]; meta: { total: number } }>('/site-docs/assembly/defects', { params: { projectId, state: 'OPEN', limit: 20, sort: 'dueDate', dir: 'asc' } })
      .then((r) => {
        if (!aktiv) return
        setZeilen(r.data.data)
        setGesamt(r.data.meta.total)
      })
      .catch(() => aktiv && setFehler(true))
    return () => {
      aktiv = false
    }
  }, [projectId])

  if (!fehler && !zeilen?.length) return null
  return (
    <div className={formCard}>
      <h2 className="mb-2 flex items-center gap-2 text-sm font-medium">
        <ListChecks className="h-4 w-4" aria-hidden /> {t('siteDocs.defects.projectCard', { count: gesamt })}
      </h2>
      {fehler && <p className="text-sm text-destructive">{t('siteDocs.defects.loadError')}</p>}
      <ul className="divide-y divide-border">
        {zeilen?.map((m) => (
          <li key={m.id} className="flex items-center gap-3 py-1.5 text-sm">
            <span className="w-24 shrink-0 font-mono text-xs text-muted-foreground">
              {m.documentNumber} · {m.nr}
            </span>
            <Link to={`/site-docs/${m.documentId}`} className="min-w-0 flex-1 truncate hover:underline">
              {m.text}
            </Link>
            <span className="shrink-0 text-xs text-muted-foreground">{[m.weight ? t(`siteDocs.assembly.weights.${m.weight}`) : null, m.dueDate ? fmtDate(m.dueDate) : null, m.taskDoneAt ? t('siteDocs.assembly.taskDone') : null].filter(Boolean).join(' · ')}</span>
          </li>
        ))}
      </ul>
      {gesamt > (zeilen?.length ?? 0) && (
        <Link to={`/site-docs/defects`} className="mt-1 block text-xs text-primary hover:underline">
          {t('siteDocs.assembly.toDefectList')}
        </Link>
      )}
    </div>
  )
}
